import { Alert, Box, SimpleGrid, Stack, Text } from '@mantine/core';
import { useMemo } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { dashboardApi } from '../../../api/dashboard';
import { useGroupQuery } from '../../../hooks/useGroupQuery';
import { PerformanceCardSkeleton } from '../PerformanceCardSkeleton';
import { mapApiToSafeTData } from './mapApiToSafeTData';
import { ClientAcquisitionCard } from './cards/ClientAcquisitionCard';
import { RevenueGeneratedCard } from './cards/RevenueGeneratedCard';
import { TulsaImpactCard } from './cards/TulsaImpactCard';
import { RiskPostureCard } from './cards/RiskPostureCard';
import { PoamPipelineCard } from './cards/PoamPipelineCard';
import type { SafeTDashboardData, SafeTOrgKey } from './types';

export function SafeTPerformanceMetrics({ selectedGroup }: { selectedGroup: string }) {
  const isDronePort = selectedGroup === 'DronePort Network';
  const isTu = selectedGroup === 'TU - SAFE-T';
  const isSafeTGroup = ['Skyway Range', 'DronePort Network', 'TU - SAFE-T'].includes(selectedGroup);
  const isSingleColumn = useMediaQuery('(max-width: 1200px)');

  const tuQuery = useGroupQuery(
    ['dashboard-metrics', 'safe-t-tu-security'],
    async () => [await dashboardApi.getDashboardMetrics('TU - SAFE-T')],
    { enabled: isSafeTGroup && isTu }
  );

  const orgQuery = useGroupQuery(
    ['dashboard-metrics', 'safe-t-org-combined'],
    async () => {
      const [skyway, droneport] = await Promise.all([
        dashboardApi.getDashboardMetrics('Skyway Range'),
        dashboardApi.getDashboardMetrics('DronePort Network'),
      ]);
      return [skyway, droneport];
    },
    { enabled: isSafeTGroup && !isTu }
  );

  const data = isTu ? tuQuery.data : orgQuery.data;
  const error = isTu ? tuQuery.error : orgQuery.error;
  const isLoading = isTu ? tuQuery.isLoading : orgQuery.isLoading;

  const dashboardData: SafeTDashboardData | null = useMemo(
    () => (!data || data.length === 0 ? null : mapApiToSafeTData(data)),
    [data]
  );

  const hasApiData = dashboardData !== null;
  const hasTuSecurityData =
    (dashboardData?.tuSecurity.risk?.length ?? 0) > 0 &&
    (dashboardData?.tuSecurity.poams?.length ?? 0) > 0;

  return (
    <Stack spacing="md">
      {error ? (
        <Alert color="yellow" variant="light">
          Unable to load dashboard API data.
        </Alert>
      ) : null}
      {!isLoading && !hasApiData ? (
        <Alert color="yellow" variant="light">
          No dashboard API data returned for this section.
        </Alert>
      ) : null}
      {isLoading && !hasApiData && isTu ? (
        <SimpleGrid
          cols={2}
          spacing="lg"
          breakpoints={[
            { maxWidth: 1200, cols: 2 },
            { maxWidth: 900, cols: 1 },
          ]}
          sx={{ alignItems: 'stretch' }}
        >
          <PerformanceCardSkeleton />
          <PerformanceCardSkeleton />
        </SimpleGrid>
      ) : null}
      {isLoading && !hasApiData && !isTu ? (
        <Stack spacing="lg">
          <SimpleGrid
            cols={2}
            spacing="lg"
            breakpoints={[
              { maxWidth: 1200, cols: 1 },
            ]}
            sx={{ alignItems: 'stretch' }}
          >
            <Box>
              <Text weight={700} mb="lg">Skyway Range</Text>
            </Box>
            <Box>
              <Text weight={700} mb="lg">DronePort Network</Text>
            </Box>
            <PerformanceCardSkeleton />
            <PerformanceCardSkeleton />
            <PerformanceCardSkeleton />
            <PerformanceCardSkeleton />
            <PerformanceCardSkeleton />
            <PerformanceCardSkeleton />
          </SimpleGrid>
        </Stack>
      ) : null}
      {hasApiData && isTu && hasTuSecurityData ? (
        <SimpleGrid
          cols={2}
          spacing="lg"
          breakpoints={[
            { maxWidth: 1200, cols: 2 },
            { maxWidth: 900, cols: 1 },
          ]}
          sx={{ alignItems: 'stretch' }}
        >
          <RiskPostureCard series={dashboardData!.tuSecurity.risk} />
          <PoamPipelineCard series={dashboardData!.tuSecurity.poams} />
        </SimpleGrid>
      ) : hasApiData && isTu ? (
        <Alert color="yellow" variant="light">
          No TU - SAFE-T risk/POAM data returned for this section.
        </Alert>
      ) : hasApiData ? (
        <Stack spacing="lg">
          {(() => {
            const selectedOrgKey: SafeTOrgKey = isDronePort ? 'droneport' : 'skyway';
            const otherOrgKey: SafeTOrgKey = isDronePort ? 'skyway' : 'droneport';
            const selectedOrgLabel = isDronePort ? 'DronePort Network' : 'Skyway Range';
            const otherOrgLabel = isDronePort ? 'Skyway Range' : 'DronePort Network';

            const renderOrgCards = (orgKey: SafeTOrgKey) => (
              <>
                <ClientAcquisitionCard
                  mode="org"
                  orgKey={orgKey}
                  series={dashboardData![orgKey].clientAcquisition}
                  goals={dashboardData![orgKey].goalsByMetric['safeT.clientAcquisition']}
                />
                <RevenueGeneratedCard
                  mode="org"
                  orgKey={orgKey}
                  series={dashboardData![orgKey].revenue}
                  goals={dashboardData![orgKey].goalsByMetric['safeT.revenue']}
                />
                <TulsaImpactCard
                  mode="org"
                  orgKey={orgKey}
                  series={dashboardData![orgKey].tulsa}
                  tulsaAllTime={dashboardData![orgKey].tulsaAllTime}
                />
              </>
            );

            if (isSingleColumn) {
              return (
                <Stack spacing="lg">
                  <Box>
                    <Text weight={700} mb="lg">{selectedOrgLabel}</Text>
                  </Box>
                  {renderOrgCards(selectedOrgKey)}
                  <Box mt="xl">
                    <Text weight={700} mb="lg">{otherOrgLabel}</Text>
                  </Box>
                  {renderOrgCards(otherOrgKey)}
                </Stack>
              );
            }

            return (
              <SimpleGrid cols={2} spacing="lg" sx={{ alignItems: 'stretch' }}>
                <Box>
                  <Text weight={700} mb="lg">{selectedOrgLabel}</Text>
                </Box>
                <Box>
                  <Text weight={700} mb="lg">{otherOrgLabel}</Text>
                </Box>
                <ClientAcquisitionCard
                  mode="org"
                  orgKey={selectedOrgKey}
                  series={dashboardData![selectedOrgKey].clientAcquisition}
                  goals={dashboardData![selectedOrgKey].goalsByMetric['safeT.clientAcquisition']}
                />
                <ClientAcquisitionCard
                  mode="org"
                  orgKey={otherOrgKey}
                  series={dashboardData![otherOrgKey].clientAcquisition}
                  goals={dashboardData![otherOrgKey].goalsByMetric['safeT.clientAcquisition']}
                />
                <RevenueGeneratedCard
                  mode="org"
                  orgKey={selectedOrgKey}
                  series={dashboardData![selectedOrgKey].revenue}
                  goals={dashboardData![selectedOrgKey].goalsByMetric['safeT.revenue']}
                />
                <RevenueGeneratedCard
                  mode="org"
                  orgKey={otherOrgKey}
                  series={dashboardData![otherOrgKey].revenue}
                  goals={dashboardData![otherOrgKey].goalsByMetric['safeT.revenue']}
                />
                <TulsaImpactCard
                  mode="org"
                  orgKey={selectedOrgKey}
                  series={dashboardData![selectedOrgKey].tulsa}
                  tulsaAllTime={dashboardData![selectedOrgKey].tulsaAllTime}
                />
                <TulsaImpactCard
                  mode="org"
                  orgKey={otherOrgKey}
                  series={dashboardData![otherOrgKey].tulsa}
                  tulsaAllTime={dashboardData![otherOrgKey].tulsaAllTime}
                />
              </SimpleGrid>
            );
          })()}
        </Stack>
      ) : null}

    </Stack>
  );
}
